import { ButtonSkeleton } from "@circleco/compass/components/ButtonSkeleton";
import { Skeleton } from "@circleco/compass/components/Skeleton";

import { classNames } from "@/react/helpers/twMergeWithCN";

/**
 * Placeholders for the upgrade page. They are decorative: the page announces
 * loading through a live region instead, so assistive tech is not read a list of
 * empty bars.
 */

/** A single figure -- an amount or a short label -- while its value reloads. */
export const UpgradeFigureSkeleton = ({
  className,
}: {
  className?: string;
}) => (
  <Skeleton
    className={classNames("h-4 w-16", className)}
    shape="pill"
    aria-hidden="true"
  />
);

/** The Current plan card's contents, before any preview has loaded. */
export const UpgradeCurrentPlanSkeleton = () => (
  <div className="mt-1 space-y-2" aria-hidden="true">
    <div className="flex items-center justify-between gap-3">
      <Skeleton className="h-5 w-32" shape="pill" />
      <Skeleton className="h-5 w-16" shape="pill" />
    </div>
    <Skeleton className="h-4 w-24" shape="pill" />
  </div>
);

/** One option card, before the price rows have loaded. */
const UpgradeOptionSkeleton = () => (
  <div
    className="border-primary flex items-start justify-between gap-4 rounded-xl border p-4"
    aria-hidden="true"
  >
    <div className="flex items-start gap-3">
      <Skeleton className="mt-1 size-4" shape="pill" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-32" shape="pill" />
        <Skeleton className="h-4 w-16" shape="pill" />
      </div>
    </div>
    <Skeleton className="h-5 w-20" shape="pill" />
  </div>
);

/** The price rows, one placeholder per price the paywall offers. */
export const UpgradeOptionsSkeleton = ({ count }: { count: number }) => (
  <div className="space-y-3" aria-hidden="true">
    {Array.from({ length: Math.max(count, 1) }, (_, index) => (
      <UpgradeOptionSkeleton key={`upgrade-option-skeleton-${index}`} />
    ))}
  </div>
);

/** The summary and CTA, before any preview has loaded. */
export const UpgradeSummarySkeleton = () => (
  <div className="w-full max-w-xl space-y-3" aria-hidden="true">
    <div className="flex justify-between gap-4">
      <Skeleton className="h-5 w-36" shape="pill" />
      <Skeleton className="h-4 w-16" shape="pill" />
    </div>
    <div className="flex justify-between gap-4">
      <Skeleton className="h-5 w-32" shape="pill" />
      <Skeleton className="h-5 w-20" shape="pill" />
    </div>
    <div className="flex justify-between gap-4">
      <Skeleton className="h-4 w-28" shape="pill" />
      <Skeleton className="h-4 w-20" shape="pill" />
    </div>
    <ButtonSkeleton fullWidth />
  </div>
);

/**
 * The confirmation strip, before the preview that names the current plan has
 * loaded. Reserving it stops the page from jumping when the real one appears.
 */
export const UpgradeBannerSkeleton = () => (
  <div
    className="bg-success-light flex w-full flex-col items-center justify-center px-5 py-3"
    aria-hidden="true"
  >
    <Skeleton className="h-5 w-64 max-w-full" shape="pill" />
    {/* The real sentence wraps onto a second line below the sm breakpoint. */}
    <Skeleton className="h-5 w-40 max-w-full sm:hidden" shape="pill" />
  </div>
);
